import React, { useEffect } from 'react'
import Modal from '../../components/Template/Modal'
import styled from '../../theme'
import Cookie from '../../assets/cookie.svg'
import { useTranslation } from 'react-i18next'
import Button from '../../components/Template/Button'
import { is } from '../../components/MediaQuery'
import { Checkbox } from '../../components/FormBuilder'
import cookiesList from './cookies'
import { isEmpty } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { getUserCookiesForce, getUserCookiesStep } from '../../reducers/userAccount/selectors/userAccount'
import { UserAccountActions } from '../../reducers/userAccount/actionNames'
import { FiX } from "react-icons/fi"
import { giveConsent } from './cookiesManager'
import { ensureCookiesVersion, markCookiesVersion } from './cookieVersion'
import { storeCookies, storeCookiesExpire } from './storage'

const Wrapper = styled.div`
    display: flex;
    position: relative;
    padding: 0px 24px 24px;
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 0px;
        padding-top: 16px;
    }
`

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 16px;
   
`

const Title = styled.div`
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px; /* 157.143% */
    letter-spacing: 0.35px;
    color: #000000;
`

const Text = styled.div<{more?: boolean}>`
    color: #000000;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 163.636% */
    margin-top: 8px;
    ${props => !props.more && `
     display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;`
  }
`

const LikeButton = styled.a`
  color: #00a541;
`

const ButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        justify-content: space-between;
    }
`

const SettingButton = styled.div`
    color: #272728;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 133.333% */
    transition: text-decoration 0.3s;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    };
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        align-self: center;
        margin-top: 16px;
        text-decoration: underline;
    }
`

const More = styled.div`
    color: #00A541;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 145.455% */
    cursor: pointer;
`

function CookiesModal({open, onClose = () => {}, hideClose = false}: ICookiesModal) {
  const [step, setStep] = React.useState(0)
  const stepStore = useSelector(getUserCookiesStep)
  const [more, setMore] = React.useState(false)
  const [cookies, setCookies] = React.useState({
    necessary: true,
    functional: false,
    analytics: false,
    performance: false,
    advertisement: false,
  })
  const {t} = useTranslation()
  const isDesktop = is.desktop()
  const force = useSelector(getUserCookiesForce)
  const dispatch = useDispatch()

  const persistExpireDate = () => {
    try {
      const expireDate = new Date()
      expireDate.setDate(expireDate.getDate() + 180)
      storeCookiesExpire(expireDate.toISOString())
    } catch (e) {}
  }

  function postToRN(message: any) {
    try {
      const w: any = window as any
      if (w.ReactNativeWebView && typeof w.ReactNativeWebView.postMessage === 'function') {
        w.ReactNativeWebView.postMessage(JSON.stringify(message))
      }
    } catch (e) {}
  }

  const handleClose = () => {
    try {
      postToRN({ type: 'cookies:close' })
      postToRN({ type: 'cookies:closed' })
    } catch (e) {}
    onClose()
  }

  useEffect(() => {
    const hasValidVersion = ensureCookiesVersion()
    if (!hasValidVersion) {
      setCookies({
        necessary: true,
        functional: false,
        analytics: false,
        performance: false,
        advertisement: false,
      })
      return
    }
    const cookies = localStorage.getItem('cookies_v2') || localStorage.getItem('cookies')
    if (cookies) {
      setCookies(JSON.parse(cookies))
    }
  }, [open])

  useEffect(() => {
    setStep(stepStore)
  }, [stepStore])

  const decline = () => {
    const nextCookies = {
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
    }
    storeCookies(nextCookies)
    try { localStorage.setItem('cookies', JSON.stringify(nextCookies)) } catch (e) {}
    markCookiesVersion()
    setCookies({
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
    })

    dispatch({type: UserAccountActions.SaveCookies})
    persistExpireDate()
    handleClose()
    giveConsent()
  }
  const accept = () => {
    const nextCookies = {
      necessary: true,
      functional: true,
      analytics: true,
      performance: true,
      advertisement: true,
    }
    storeCookies(nextCookies)
    try { localStorage.setItem('cookies', JSON.stringify(nextCookies)) } catch (e) {}
    markCookiesVersion()
    setCookies({
      necessary: true,
      functional: true,
      analytics: true,
      performance: true,
      advertisement: true,
    })

    dispatch({type: UserAccountActions.SaveCookies})
    persistExpireDate()
    handleClose()
    giveConsent()
  }
  const save = () => {
    storeCookies(cookies)
    try { localStorage.setItem('cookies', JSON.stringify(cookies)) } catch (e) {}
    markCookiesVersion()
    dispatch({type: UserAccountActions.SaveCookies})
    persistExpireDate()
    giveConsent()
    handleClose()
  }
  const toggle = (name) => () => {
    setCookies({
      ...cookies,
      [name]: !cookies[name]
    })
  }
 return (
   <Modal
     style={{
       maxWidth: '643px',
       width: '100vw',
       margin: '0',
       maxHeight: '100vh',
     }}
     open={open}
     onClose={() => {}}
   >
     <Wrapper>
      {force && !hideClose && <FiX style={{
        position: 'absolute',
        right: '0px',
        cursor: 'pointer',
        top:'0px',
      }} onClick={handleClose}/>}
       {(step === 0 || isDesktop) && <div>
       <img src={Cookie} alt={'cookie'} />
       </div>}
       {step === 0 && <ContentWrapper>
         <Title>{t('BED_22412_baner_text_1')}</Title>
         <Text more={true}>{t('BED_22412_baner_text_2')}{' '}{t('BED_22412_baner_text_3')}{' '}{t('BED_22412_baner_text_4')}{' '}{t('BED_22412_baner_text_5')}{' '}
         <LikeButton target={'_blank'} href={t('BED_22412_baner_cookie_policy_link')}>{t('BED_22412_baner_cookie_policy_text')}</LikeButton>{' '}{t('BED_22412_baner_text_6')}{' '}<LikeButton target={'_blank'} href={t('BED_3621_privacy_policy_link')}>{t('BBO_2894_privacy_policy')}</LikeButton></Text>
         <ButtonsWrapper style={{marginTop: isDesktop?'16px':'24px'}}>
           {isDesktop && <SettingButton onClick={() => setStep(1)}>{t('BED_22412_baner_customize_button')}</SettingButton>}
           <div style={{width: isDesktop? 'auto':'47%', marginLeft:isDesktop?'auto':'unset'}}>
           <Button
             size={isDesktop? 0.9:1}
             light={true}
             style={{
               width: '100%',
               color: '#FFF',
               backgroundColor:'#A1A1A1',
               fontSize: '12px',
               fontStyle: 'normal',
               fontWeight: 500,
               lineHeight: '16px',
               height: '34px',
               padding: '7px 16px'
             }}
             onClick={decline}
           >
             {t('BED_22412_baner_decline_button')}
           </Button>
           </div>
           <div style={{width: isDesktop? 'auto':'47%'}}>
           <Button
             size={isDesktop? 0.9:1}
             style={{
               width: '100%',
               marginLeft: isDesktop ? '25px' : 'unset',
               fontSize: '12px',
               fontStyle: 'normal',
               fontWeight: '500',
               lineHeight: '20px',
               height: '34px',
               padding: '7px 16px'
             }}
             primary={true}
             onClick={accept}
           >
             {t('BED_22412_baner_accept_button')}
           </Button>
           </div>
         </ButtonsWrapper>
         {!isDesktop && <SettingButton onClick={() => setStep(1)}>{t('BED_22412_baner_customize_button')}</SettingButton>}
       </ContentWrapper>}
       {step === 1 && <ContentWrapper style={{paddingLeft: (step === 1 && !isDesktop)? '0px':'16px'}}>
         <Title>{t('BED_22412_customize_header')}</Title>
         <Text more={more}>
           {t('BED_22412_customize_text_1')}{' '}
           <span style={{margin:'16px 0px', display:'block'}}>
           {t('BED_22412_customize_text_2')}
           </span>
           {t('BED_22412_customize_text_3')}{' '}
           {t('BED_22412_customize_text_4')}
         </Text>
         <More style={{marginBottom:'10px'}} onClick={() => setMore(!more)}>{t(more? 'BED_3197_panel_7':'BED_22412_learn_more')}</More>
       </ContentWrapper>}
     </Wrapper>
     {step === 1 && <CookiesSection>
       <CookieSection cookies={cookiesList['necessary']} enabled={cookies['necessary']} name={'necessary'} toggle={toggle} always={true} desc={'BED_22412_customize_necessary_text'} title={'BED_22412_customize_necessary_header'}></CookieSection>
       <CookieSection cookies={cookiesList['functional']} enabled={cookies['functional']} name={'functional'} toggle={toggle} desc={'BED_22412_customize_functional_text'} always={false} title={'BED_22412_customize_functional_header'}></CookieSection>
       <CookieSection cookies={cookiesList['analytics']} enabled={cookies['analytics']} name={'analytics'} toggle={toggle} desc={'BED_22412_customize_analytics_text'} always={false} title={'BED_22412_customize_analytics_header'}></CookieSection>
       <CookieSection cookies={cookiesList['performance']} enabled={cookies['performance']} name={'performance'} toggle={toggle} desc={'BED_22412_customize_performance_text'} always={false} title={'BED_22412_customize_performance_header'}></CookieSection>
       <CookieSection cookies={cookiesList['advertisement']} enabled={cookies['advertisement']} name={'advertisement'} toggle={toggle} desc={'BED_22412_customize_advertisement_header'} always={false} title={'BED_22412_customize_advertisement_header'}></CookieSection>
     </CookiesSection>}
     {step === 1 && <><ButtonsWrapper>
       {isDesktop && <div style={{ marginLeft: 'auto' }}>
         <Button
           size={0.9}
           light={true}
           style={{
             width: '100%',
             color: '#FFF',
             backgroundColor:'#A1A1A1',
             fontSize: '12px',
             fontStyle: 'normal',
             fontWeight: 500,
             lineHeight: '16px',
             height: '34px',
             padding: '7px 16px'
           }}
           onClick={decline}
         >
           {t('BED_22412_baner_decline_button')}
         </Button>
       </div>}
       <div style={{
         width:isDesktop?'unset':'100%',
       }}>
         <Button
           size={0.9}
           style={{
             marginLeft: isDesktop ? '25px' : 'unset',
             marginRight: isDesktop ? 'unset' : '12px',
             fontSize: '12px',
             fontStyle: 'normal',
             fontWeight: '500',
             lineHeight: '20px',
             height: '34px',
             padding: '7px 16px'
           }}
           primary={true}
           onClick={save}
         >
           {t('BED_22412_baner_save_preferences_button')}
         </Button>
       </div>
       <div style={{
         width:isDesktop?'unset':'100%',
       }}>
         <Button
           size={0.9}
           style={{
             marginLeft: isDesktop ? '25px' : '12px',
             fontSize: '12px',
             fontStyle: 'normal',
             fontWeight: '500',

             lineHeight: '20px',
             height: '34px',
             padding: '7px 16px'
           }}
           primary={true}
           onClick={accept}
         >
           {t('BED_22412_baner_accept_button')}
         </Button>
       </div>
     </ButtonsWrapper>
       {!isDesktop && <div style={{alignSelf:'center', marginTop:'16px'}}>
         <Button
           size={0.9}
           light={true}
           style={{
             width: '100%',
             color: '#272728',
             backgroundColor:'transparent',
             fontSize: '12px',
             fontStyle: 'normal',
             fontWeight: 500,
             lineHeight: '16px',
           }}
           onClick={decline}
         >
           {t('BED_22412_baner_decline_button')}
         </Button>
       </div>}
     </>}

   </Modal>
 );
}

const CookiesSection = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-height: 62vh;
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        max-height: 70vh;
    }
`

const CookieSectionWrapper = styled.div`
    display: flex;
    margin: 0px 0px 16px;
    padding: 8px 16px;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 6px;
    border: 1px solid #D7D7D7;
    align-self: stretch;
`

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`
const CookieSectionTitle = styled.div`
    color: #272728;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px; /* 150% */
`

const Always = styled.div`
    color: #00A541;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 140% */
`

const Desc = styled.div`
    color: #272728;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 145.455% */
`

const CookieSection = ({name, toggle, title, enabled = false, desc, always, cookies = []}: any) => {
  const [more, setMore] = React.useState(false)
  const {t} = useTranslation()
  return <CookieSectionWrapper>
    <TitleWrapper><CookieSectionTitle>
      {t(title)}
    </CookieSectionTitle>
      {always? <Always>{t('BED_22412_customize_necessary_status')}</Always>:  <Checkbox.Field
        value={enabled}
        checked={enabled}
        onChange={toggle(name)}
        onClick={toggle(name)}
        wrapperStyle={{width:'unset'}}
        name={'enabled'}
        noSpacing={true}
      />}
    </TitleWrapper>
    <Desc>
      {t(desc)}
    </Desc>
    {more && <>
      <Divider/>
      <div style={{
        maxHeight: '500px',
        overflowY: 'auto',
        paddingRight:'8px'
      }}>
      {cookies.map(c => (<CookieWrapper>
        <CookieTitleWrapper><CookieHeader>{t('BED_22412_customize_cookie_label')}</CookieHeader><CookieTitle>{c.title}</CookieTitle></CookieTitleWrapper>
        <CookieTitleWrapper><CookieHeader>{t('BED_22412_customize_cookie_duration')}</CookieHeader><CookieTitle>{t(c.time)}</CookieTitle></CookieTitleWrapper>
        <CookieDesc>{t(c.desc)}</CookieDesc>
        <Divider/>
    </CookieWrapper>))}
      </div>
    </>}
    {!isEmpty(cookies) && <More onClick={() => setMore(!more)}>{t(more? 'BED_3197_panel_7':'show_details')}</More>}
  </CookieSectionWrapper>
}

const CookieWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

const CookieTitleWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`

const CookieHeader = styled.div`
    color: #272728;

    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 140% */
`

const CookieTitle = styled.div`
    color: #272728;

    text-align: right;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 14px; /* 140% */
`

const CookieDesc = styled.div`
    color: #000000;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-style: italic;
    font-weight: 400;
    line-height: 16px; /* 145.455% */
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #efefef;
    margin: 4px 0px;
`

export interface ICookiesModal {
  open?: boolean
  onClose?: () => void
  hideClose?: boolean
}

export default CookiesModal
